import React from 'react';

import { TextButton } from '@components/Button/variants';
import Highlight from '@components/FeedItems/Highlight';
import IconArrow from '@components/Images/IconArrow';
import IconLock from '@components/Images/Rebranding/IconLock';
import NameList from '@components/NameList';
import PaywallFullBleed from '@components/Paywall/FullBleed';
import { briefing_feed_path, briefing_path } from '@routes';
import { FeedContentType } from '@utils/enums';
import { articlePath } from '@utils/queryStringHelper';

import { BriefingPaywallProps, BriefingType } from '.';

interface LockedBriefingProps {
  briefing: BriefingType;
  paywallProps: BriefingPaywallProps;
}

const LockedBriefing = ({
  briefing: { authors, featuredEntityTopic, headline, relatedItems },
  paywallProps,
}: LockedBriefingProps): JSX.Element => {
  return (
    <div className="relative mx-auto w-full max-w-[1154px] p-16 font-normal">
      <p className="text m-0 min-h-36 text-center font-suisse-intl-mono text-overline-md font-normal text-grayscale-charcoal sm:text-caption-lg md:text-feed-headline-sm">
        {paywallProps?.title
          ? paywallProps.title
          : featuredEntityTopic && (
              <>Behind the scenes of the biggest news in {featuredEntityTopic}</>
            )}
      </p>
      <div className="mx-auto mt-16 grid w-full grid-cols-1 grid-rows-[auto_auto_auto] gap-16 sm:max-w-3xl md:mt-24 md:max-w-none md:grid-cols-[1fr_max-content_1fr] md:grid-rows-1 md:gap-x-32 md:px-24">
        <header className="md:col-start-1">
          <Highlight
            highlight="Briefing"
            customPath={briefing_feed_path()}
            className="!text-caption-md"
          />
          <h1 className="m-0 font-suisse-works text-headline-xs md:text-headline-md">{headline}</h1>
          <span className="font-suisse-screen text-disclosure-md text-grayscale-charcoal">
            <NameList authors={authors} authorClassName="author-name-body" />
          </span>
          {relatedItems?.length > 0 && (
            <div>
              <p className="mb-16 mt-32 font-suisse-intl-mono text-overline-sm lg:mt-48">
                Continued reading
              </p>
              <div className="h-px border-0 border-b border-solid border-b-grayscale-beige"></div>
              {relatedItems.map((item) => (
                <div className="mt-16 flex" key={item.id}>
                  <IconArrow className="mr-16 h-20 w-20 rotate-180" />
                  <TextButton
                    as="link"
                    href={
                      item.type === FeedContentType.Article
                        ? articlePath({ slug: item.slug, newsletterSlug: item.newsletterSlug })
                        : briefing_path(item.slug)
                    }
                    className="!justify-start !font-suisse-neue !text-body-md no-underline"
                  >
                    {item.title}
                  </TextButton>
                </div>
              ))}
            </div>
          )}
        </header>
        <div className="relative row-start-2 m-auto flex w-full text-center font-suisse-intl-mono text-overline-sm after:absolute after:left-0 after:top-1/2 after:h-px after:w-full after:-translate-y-1/2 after:bg-grayscale-charcoal after:content-[''] md:col-start-2 md:row-start-1 md:h-full md:w-auto md:text-overline-md after:md:left-1/2 after:md:h-full after:md:w-px after:md:-translate-x-1/2">
          <span className="relative z-10 m-auto inline bg-white p-8 text-grayscale-charcoal">
            <IconLock className="h-12 w-12 pt-px" />
          </span>
        </div>
        <div className="row-start-3 self-center md:col-start-3 md:row-start-1">
          <PaywallFullBleed isPro={false} {...paywallProps} />
        </div>
      </div>
    </div>
  );
};

export default LockedBriefing;
